import { Button, ButtonGroup, Paper, Stack, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { sum, uniq } from 'ramda'
import { useTranslation } from 'react-i18next'
import { Link, generatePath, useNavigate } from 'react-router-dom'

import { FieldModel } from '@cibo/core'
import { ProgramIcon, useAllProgramContent, usePrograms } from '@cibo/programs'

import { ProfileFieldsAlert } from 'components'
import { ROUTE } from 'navigation/types'

import { ProgramStatusIcon } from 'components/ProgramStatusIcon'
import { ChevronRight } from 'mdi-material-ui'
import { ENROLLMENT_ROUTES, PROGRAM_ROUTES } from 'navigation/types/routes'
import { SectionHeader } from '../SectionHeader'
import { ProgramsOverviewSkeleton } from './ProgramsOverviewSkeleton'

interface ProgramsOverviewProps {
  field: FieldModel
}

export const ProgramsOverview = ({ field }: ProgramsOverviewProps) => {
  const { t } = useTranslation('LandReport/ProgramsOverview')
  const programContent = useAllProgramContent()
  const navigate = useNavigate()

  const configs = usePrograms()

  if (programContent.isLoading || configs.isLoading) {
    return (
      <Stack spacing={2}>
        <SectionHeader title={t('programsTitle')} subtitle={t('programsSubtitleLoading')} />
        <Paper elevation={0}>
          <Stack spacing={3} padding={2}>
            <ProgramsOverviewSkeleton />
            <ProgramsOverviewSkeleton />
            <ProgramsOverviewSkeleton />
          </Stack>
        </Paper>
      </Stack>
    )
  }

  const activePrograms = field.programStatuses?.filter(({ participating }) => !!participating)
  const workflowPrograms = field.programStatuses?.filter(({ workflow }) => !!workflow)
  const now = new Date()
  const recommendedPrograms = field.programs?.filter(
    ({ recommendationScore, programId }) =>
      recommendationScore > 0 &&
      !activePrograms?.find(p => p.programId === programId) &&
      !workflowPrograms?.find(p => p.programId === programId) &&
      configs.data?.find(p => {
        const closeDate = new Date(p.program.enrollmentWindow.closeDate)
        return p.programId === programId && closeDate > now
      })
  )

  return (
    <Stack spacing={4}>
      <ProfileFieldsAlert
        fieldId={field.resourceId}
        hasMatches={
          (activePrograms && activePrograms.length > 0) ||
          (recommendedPrograms && recommendedPrograms.length > 0)
        }
        onClick={() => navigate(generatePath(ROUTE.PROFILE_FIELD, { fieldId: field.resourceId }))}
      />
      {workflowPrograms && workflowPrograms.length > 0 && (
        <Stack spacing={2}>
          <SectionHeader
            title={t('openEnrollments', { count: workflowPrograms.length })}
            subtitle={t('openEnrollmentsSubtitle', { count: workflowPrograms.length })}
          />
          <Paper elevation={0}>
            {workflowPrograms.map(p => {
              const program = configs.data?.find(({ programId }) => p.programId === programId)
              const status = field.programStatuses?.find(s => s.programId === p.programId)
              return !!program ? (
                <ButtonGroup fullWidth key={p.programId}>
                  {!!status && <ProgramStatusIcon status={status} benchmark="enrollment" />}

                  <Button
                    key={p.programId}
                    component={Link}
                    to={generatePath(ENROLLMENT_ROUTES.DETAIL, { workflowId: p.workflow })}
                    fullWidth
                    sx={{ justifyContent: 'flex-start' }}
                    variant="text"
                  >
                    <Stack direction="row" flex={1} alignItems="center" spacing={2}>
                      <Stack direction="row" alignItems="center" flex={1} spacing={2}>
                        <Stack direction="row" alignItems="center">
                          <ProgramIcon program={program.program} size={32} />
                        </Stack>
                        <Typography>{program.program.content.fields.title}</Typography>
                      </Stack>
                      <ChevronRight />
                    </Stack>
                  </Button>
                </ButtonGroup>
              ) : null
            })}
          </Paper>
        </Stack>
      )}
      {activePrograms && activePrograms.length > 0 && (
        <Stack spacing={2}>
          <SectionHeader
            title={t('programsTitle')}
            subtitle={t('programsSubtitle', { count: activePrograms.length })}
          />
          <Paper elevation={0}>
            <Stack>
              {activePrograms.map(({ programId }) => {
                const program = configs.data?.find(p => p.programId === programId)
                const date = field.program(programId)?.enrolledAt
                return !!program ? (
                  <>
                    <Button
                      key={programId}
                      component={Link}
                      to={generatePath(PROGRAM_ROUTES.DETAIL, {
                        programId,
                      })}
                      fullWidth
                      sx={{ justifyContent: 'flex-start' }}
                    >
                      <Stack direction="row" flex={1} alignItems="center" spacing={2}>
                        <Stack direction="row" alignItems="center" flex={1} spacing={2}>
                          <Stack direction="row" alignItems="center">
                            <ProgramIcon program={program.program} size={32} />
                          </Stack>
                          <Stack>
                            <Typography>{program.program.content.fields.title}</Typography>
                            {!!date && (
                              <Typography variant="caption">
                                {t('enrollmentDate', { date: new Date(date) })}
                              </Typography>
                            )}
                          </Stack>
                        </Stack>
                        <ChevronRight />
                      </Stack>
                    </Button>
                  </>
                ) : null
              })}
            </Stack>
          </Paper>
        </Stack>
      )}
      {recommendedPrograms && recommendedPrograms.length > 0 && (
        <Stack spacing={2}>
          <SectionHeader
            title={t('recommendedProgramsTitle')}
            subtitle={t('recommendedProgramsSubtitle', { count: recommendedPrograms.length })}
          />
          <Paper elevation={0}>
            <Box>
              {recommendedPrograms.map(({ programId }) => {
                const config = configs.data?.find(p => p.programId === programId)
                const dollars = !!programId
                  ? sum(field.programDollarPerAcrePotential(programId).map(a => a.value || 0)) *
                    field.acres
                  : undefined
                const credits = !!programId
                  ? sum(field.programCreditPerAcrePotential(programId).map(a => a.credits || 0)) *
                    field.acres
                  : undefined
                const years = !!programId
                  ? uniq(field.programPerAcrePotential(programId)?.map(a => a.year) || [])
                  : []

                const potential =
                  !!dollars && !!credits
                    ? t('potential_dollars_credits', { dollars, credits, years: years.length })
                    : !!dollars
                    ? t('potential_dollars', { dollars, years: years.length })
                    : !!credits
                    ? t('potential_credits', { credits, years: years.length })
                    : t('learnMore')

                return config?.program ? (
                  <Button
                    key={programId}
                    component={Link}
                    to={generatePath(ROUTE.PROGRAM, { programId })}
                    fullWidth
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    <Stack direction="row" flex={1} alignItems="center" spacing={2}>
                      <Stack direction="row" flex={1} spacing={2}>
                        <ProgramIcon program={config.program} size={32} />
                        <Stack flex={1}>
                          <Typography>{config.program.content.fields.title}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {potential}
                          </Typography>
                        </Stack>
                      </Stack>
                      <ChevronRight />
                    </Stack>
                  </Button>
                ) : null
              })}
            </Box>
          </Paper>
        </Stack>
      )}
    </Stack>
  )
}
